import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AuthService } from '../../services/auth.service';
import { ModalService } from '../../services/modal.service';

interface Sample {
  id: number;
  name: string;
  documentType: string;
  pageRange: string;
  firstPage: number;
  lastPage: number;
  createdDate: Date;
  pdfData?: Uint8Array;
}

interface SortingRule {
  id: number;
  field: string;
  order: 'asc' | 'desc';
}

@Component({
  selector: 'app-versioning',
  standalone: true,
  imports: [CommonModule, FormsModule, PdfViewerModule],
  templateUrl: './versioning.component.html',
  styleUrl: './versioning.component.css'
})
export class VersioningComponent implements OnInit {
  // View mode: 'table' or 'configuration'
  viewMode: 'table' | 'configuration' = 'table';

  // Table view - versioning documents list
  versioningDocuments: any[] = [];

  // Configuration view - Samples
  samples: Sample[] = [];
  selectedSample: Sample | null = null;
  currentSampleIndex: number = 0;
  totalSamples: number = 0;

  // PDF Viewer
  pdfSrc: Uint8Array | null = null;
  totalPages: number = 0;
  currentPage: number = 1;
  zoom: number = 1.0;

  // Sorting
  sortingRules: SortingRule[] = [];
  availableSortFields: string[] = [
    'Statement Start Date',
    'Statement End Date',
    'Borrower Name',
    'Social Security'
  ];

  // Group By
  groupByField: string = '';
  groupByOptions: string[] = [
    'Statement Start Date',
    'Statement End Date',
    'Borrower Name',
    'Social Security'
  ];
  groupPrefix: string = '';

  // Nomenclature
  nomenclature: string = 'v/final';
  nomenclatureOptions: string[] = ['v/final', 'initial/v/final', 'v'];
  isAuthenticated: boolean = false;

  constructor(
    public router: Router,
    public authService: AuthService,
    private modalService: ModalService
  ) {
  }

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();
    if (this.viewMode === 'table') {
      this.loadVersioningDocuments();
    } else {
      this.loadSamples();
      this.addSortingRule();
    }
  }

  loadVersioningDocuments(): void {
    this.versioningDocuments = [];
  }

  loadSamples(): void {
    this.samples = [];
    this.totalSamples = 0;
  }

  selectSample(sample: Sample, index: number): void {
    this.selectedSample = sample;
    this.currentSampleIndex = index;

    // Load PDF if available
    if (sample.pdfData) {
      this.pdfSrc = sample.pdfData;
      this.currentPage = sample.firstPage;
    } else {
      this.pdfSrc = null;
    }
  }

  addSortingRule(): void {
    const newRule: SortingRule = {
      id: Date.now(),
      field: '',
      order: 'asc'
    };
    this.sortingRules.push(newRule);
  }

  removeSortingRule(id: number): void {
    this.sortingRules = this.sortingRules.filter(rule => rule.id !== id);
  }

  saveConfiguration(): void {
    const config = {
      sorting: this.sortingRules.filter(rule => rule.field !== ''),
      groupBy: this.groupByField,
      groupPrefix: this.groupPrefix,
      nomenclature: this.nomenclature
    };
    alert('Configuration saved successfully!');
  }

  // PDF Viewer methods
  onPdfLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  zoomIn(): void {
    if (this.zoom < 3.0) {
      this.zoom += 0.1;
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom -= 0.1;
    }
  }

  // Table view methods
  onRowDoubleClick(doc: any): void {
    this.viewMode = 'configuration';
    this.loadSamples();
    this.addSortingRule();
  }

  backToTable(): void {
    this.viewMode = 'table';
    this.loadVersioningDocuments();
  }

  openNewDocDialog(): void {
    alert('Open new versioning document dialog');
  }

  // Navigation
  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data-extraction']);
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  navigateToExtractionEval(): void {
    this.router.navigate(['/dashboard']);
  }

  logout(): void {
    // Show logout confirmation modal (Scenario 3)
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  /**
   * Get current user email from sessionStorage
   */
  getUserEmail(): string {
    return sessionStorage.getItem('userEmail') || '';
  }

  getUsernameFromEmail(email: string = ''): string {
    const userEmail = email || this.getUserEmail();
    if (!userEmail) return '';
    const atIndex = userEmail.indexOf('@');
    return atIndex > 0 ? userEmail.substring(0, atIndex) : userEmail;
  }
}
